import { useEffect, useMemo, useState } from 'react';
import { dataService } from '@/services/dataService';
import { ActivityEvent } from '@/types/entities';
import { useI18n } from '@/i18n';

const ACTOR_FILTERS = ['all', 'agent', 'user', 'system'] as const;
const RESULT_FILTERS = ['all', 'success', 'warn', 'error'] as const;

/**
 * Real entries only — every row here comes from pushActivity() calls made
 * by the actual CRUD flows across Creators/Follow-ups/Approvals/
 * Partnerships/Contracts/Agents/Exports, not a static seeded feed.
 */
export function ActivityPage() {
  const { t } = useI18n();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [actorFilter, setActorFilter] = useState<(typeof ACTOR_FILTERS)[number]>('all');
  const [resultFilter, setResultFilter] = useState<(typeof RESULT_FILTERS)[number]>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    dataService.listActivity().then(setEvents);
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events
      .filter((e) => actorFilter === 'all' || e.actorType === actorFilter)
      .filter((e) => resultFilter === 'all' || e.result === resultFilter)
      .filter((e) => !q || e.action.toLowerCase().includes(q) || e.actorLabel.toLowerCase().includes(q) || (e.targetEntityId ?? '').toLowerCase().includes(q))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [events, actorFilter, resultFilter, query]);

  return (
    <div>
      <h1>{t('nav.activity')}</h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {ACTOR_FILTERS.map((f) => (
          <button key={f} onClick={() => setActorFilter(f)} className={actorFilter === f ? 'btn-primary' : ''}>{f}</button>
        ))}
        <span style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 4px' }} />
        {RESULT_FILTERS.map((f) => (
          <button key={f} onClick={() => setResultFilter(f)} className={resultFilter === f ? 'btn-primary' : ''}>{f}</button>
        ))}
        <input
          className="text-input"
          placeholder={t('common.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginInlineStart: 'auto', width: 220 }}
        />
      </div>
      <div className="card" style={{ padding: 8 }}>
        {visible.length === 0 && <div style={{ padding: 16, color: 'var(--text-3)', fontSize: 13 }}>{events.length === 0 ? 'No activity yet — actions you take across the app will appear here.' : 'No activity matches this filter yet.'}</div>}
        {visible.map((e) => (
          <div key={e.id} style={{ display: 'flex', gap: 10, padding: '10px 8px', borderBottom: '1px solid var(--border)' }}>
            <span className={`badge ${e.result}`}>{e.actorLabel}</span>
            <span style={{ flex: 1 }}>{e.action}{e.targetEntityId ? ` — ${e.targetEntityId}` : ''}</span>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{new Date(e.createdAt).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
